import { create } from 'zustand'
import type { BankId } from '@busswerks/shared'

type ViewMode = 'graph' | 'matrix' | 'list'

/**
 * Visual themes. Each maps to a `theme-<id>` class on <html> and a
 * CSS file under src/styles/themes/<id>.css.
 */
export type ThemeId =
  | 'terminal-crt'
  | 'groovebox'
  | 'neon-modular'
  | 'vintage-console'

export const THEMES: ReadonlyArray<{ id: ThemeId; label: string }> = [
  { id: 'terminal-crt', label: 'Terminal / CRT' },
  { id: 'groovebox', label: 'Groovebox' },
  { id: 'neon-modular', label: 'Neon Modular' },
  { id: 'vintage-console', label: 'Vintage Console' },
]

const THEME_STORAGE_KEY = 'ac-theme'
const DEFAULT_THEME: ThemeId = 'terminal-crt'

/**
 * LocalStorage key for the persisted count-in bars setting. Kept
 * as a separate key from theme so the two persist independently
 * and legacy values don't collide. Accepts 0, 1, 2, 4 only;
 * anything else normalises back to 0 on read.
 */
const COUNT_IN_STORAGE_KEY = 'ac-record-count-in-bars'
export type CountInBars = 0 | 1 | 2 | 4
const DEFAULT_COUNT_IN: CountInBars = 0

function readStoredCountIn(): CountInBars {
  if (typeof window === 'undefined') return DEFAULT_COUNT_IN
  try {
    const raw = window.localStorage.getItem(COUNT_IN_STORAGE_KEY)
    const n = Number(raw)
    if (n === 0 || n === 1 || n === 2 || n === 4) return n
    return DEFAULT_COUNT_IN
  } catch {
    return DEFAULT_COUNT_IN
  }
}

/**
 * Bussboard canvas visual skin. `clean` is today's node-graph look
 * (React Flow defaults + our surface tokens). `switchboard` is the
 * opt-in patchbay skin per `docs/DESIGN_bussboard_switchboard.md` —
 * jack-plate nodes, cable-style edges, rack-panel background.
 * Persisted so the user's choice survives reloads. Starts at
 * `clean` on first load so switchboard never surprises users who
 * haven't opted in.
 */
export type CanvasTheme = 'clean' | 'switchboard'
const CANVAS_THEME_STORAGE_KEY = 'ac-canvas-theme'
const DEFAULT_CANVAS_THEME: CanvasTheme = 'clean'

function readStoredCanvasTheme(): CanvasTheme {
  if (typeof window === 'undefined') return DEFAULT_CANVAS_THEME
  try {
    const raw = window.localStorage.getItem(CANVAS_THEME_STORAGE_KEY)
    return raw === 'switchboard' ? 'switchboard' : DEFAULT_CANVAS_THEME
  } catch {
    return DEFAULT_CANVAS_THEME
  }
}

/**
 * Which "main pane" tab is selected in the sequencer — the row that
 * sits above the editor canvas and switches between the grid layouts
 * (2×8 / 8×8 / 4×4 / Tracker), Piano Roll, Automation, and Plugin.
 * When `'grid'`, the tab defers to `sequencerStore.layoutMode` for
 * which of the four pad/tracker layouts renders. Persisted so the
 * user lands back on their last view after a reload.
 */
export type MainPaneTab = 'grid' | 'piano-roll' | 'automation' | 'plugin'
const MAIN_PANE_TAB_STORAGE_KEY = 'ac-main-pane-tab'
const DEFAULT_MAIN_PANE_TAB: MainPaneTab = 'grid'

function readStoredMainPaneTab(): MainPaneTab {
  if (typeof window === 'undefined') return DEFAULT_MAIN_PANE_TAB
  try {
    const raw = window.localStorage.getItem(MAIN_PANE_TAB_STORAGE_KEY)
    if (
      raw === 'grid' ||
      raw === 'piano-roll' ||
      raw === 'automation' ||
      raw === 'plugin'
    ) {
      return raw
    }
    return DEFAULT_MAIN_PANE_TAB
  } catch {
    return DEFAULT_MAIN_PANE_TAB
  }
}

/**
 * Which plugin (if any) is currently displayed in the main-pane
 * `Plugin` tab. `null` → the tab renders its empty-state panel.
 *
 * Populated by the "Expand" button on a bus FX chain row (see
 * `BusEffectChain`). The tuple uniquely identifies a live plugin
 * instance: `busId` locates the bus, `effectId` the slot on that
 * bus. The Plugin tab walks `busRegistry.getUserBus(busId)` →
 * `bus.getPluginHandle(effectId)` on every render to resolve the
 * `PluginHandle` + thence the analyzer id.
 *
 * Persisted so that reopening the editor lands on the same
 * analyzer if the underlying bus + effect still exist — if they
 * don't, the Plugin tab silently falls back to the empty state.
 */
export interface SelectedPlugin {
  busId: string
  effectId: string
}
const SELECTED_PLUGIN_STORAGE_KEY = 'ac-selected-plugin'

function readStoredSelectedPlugin(): SelectedPlugin | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(SELECTED_PLUGIN_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<SelectedPlugin>
    if (
      typeof parsed.busId === 'string' &&
      typeof parsed.effectId === 'string'
    ) {
      return { busId: parsed.busId, effectId: parsed.effectId }
    }
    return null
  } catch {
    return null
  }
}

/**
 * Whether the main-pane tab's current view is rendered in the
 * full-screen modal overlay. Session-only (not persisted) — full
 * screen is a transient workspace state; landing in it after a
 * reload would feel surprising. Reset to false every time the page
 * mounts.
 */
const DEFAULT_FULL_SCREEN = false

/**
 * Tri-state mode for the pad grid's click-handling:
 *
 *   - `'normal'` (default) → each pad plays its own assigned voice
 *     at the pad's own note. Performance mode.
 *   - `'keys'` → pads turn into a chromatic / scale keyboard that
 *     plays the SOURCE pad's voice at each key's scaled note.
 *     Coordinated with `project.keysMode.enabled` via `setPadMode`
 *     — flipping the UI to 'keys' turns the project's keysMode on,
 *     flipping off turns it off.
 *   - `'pattern'` → SP-404-style pattern select. Each pad maps to
 *     the Nth pattern in the current bank's ordered list. Tap pad N
 *     to queue pattern N for the next downbeat via
 *     `queuePatternSwitch`. Capped at 16 patterns per bank.
 *
 * Ephemeral — live performance state; doesn't round-trip with the
 * project. If we later want Keys/Pattern mode to persist with a
 * saved project we can thread it through the schema then.
 */
export type PadMode = 'normal' | 'keys' | 'pattern'
const DEFAULT_PAD_MODE: PadMode = 'normal'

/**
 * Whether the right-side performance FX rail (Scrambler + FxBank —
 * filter/delay/reverb/bitcrush/tape-stop/stutter) is collapsed down
 * to a narrow strip so the main pane has more horizontal room.
 * Persisted to localStorage (layout preference, survives reloads).
 */
const FX_RAIL_COLLAPSED_STORAGE_KEY = 'ac-fx-rail-collapsed'
function readStoredFxRailCollapsed(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(FX_RAIL_COLLAPSED_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Scrub Pad → bank-override performance glue. Two pieces of
 * ephemeral state:
 *
 *   - `scrubEngaged` → true whenever the Scrub Pad has any zone
 *     held OR its Latch toggle is on. The ScrubPad component owns
 *     the write; the sequencer triggers hook reads to decide
 *     whether to suppress the target bank's pad audio.
 *   - `scrubTargetBankId` → which bank the scrub override applies
 *     to. `null` → no override (legacy behaviour; scrub + pattern
 *     play in parallel). Set via the bank picker in the Scrub Pad
 *     panel.
 *
 * Both are session-only for v1 (matches `padMode` / `fullScreen`).
 * Persisting would need a decision on whether the override is
 * project-scoped or global; revisit when users request it.
 */

function isThemeId(value: unknown): value is ThemeId {
  return THEMES.some(t => t.id === value)
}

/**
 * Resolve the theme for this session. Any pre-existing legacy value
 * (`'dark'` / `'light'` from before the 4-theme picker shipped) is
 * silently migrated to the default.
 */
function readStoredTheme(): ThemeId {
  if (typeof window === 'undefined') return DEFAULT_THEME
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
    if (isThemeId(stored)) return stored
    // Migration: older stores used 'dark'/'light'; fall through to default.
    return DEFAULT_THEME
  } catch {
    return DEFAULT_THEME
  }
}

const initialTheme = readStoredTheme()

interface UIStoreState {
  viewMode: ViewMode
  selectedIds: Set<string>
  expandedPortGroups: Set<string>
  theme: ThemeId
  focusedZoneId: string | null
  aiPanelOpen: boolean
  showGrid: boolean
  /**
   * Pad index currently in "record to this pad" mode, or null when
   * no pad is armed. Ephemeral — deliberately NOT persisted, because
   * restoring a half-armed pad across page reloads would feel
   * unpredictable. Enforces mutual exclusion: only one pad can be
   * actively recording at a time (other PadRecordButtons disable
   * while this is set to any index).
   */
  recordingPadIndex: number | null
  /**
   * When true, the next pad trigger starts recording INTO that pad
   * instead of auditioning / playing it. Cleared automatically on
   * the intercept so the flag is single-shot (tap ARM, tap a pad,
   * ARM clears itself while recording runs). Pressing ARM again
   * during a recording cancels the in-progress take.
   *
   * Ephemeral — not persisted, because an armed state waiting for
   * a pad tap across a reload would be surprising.
   */
  recordArmNext: boolean
  /**
   * Number of BPM-synced bars of click-track to play BEFORE a
   * recording actually starts. `0` disables the count-in.
   * Persisted — this is a user preference, not an ephemeral flag.
   */
  recordCountInBars: CountInBars
  /**
   * True while the armed pad's count-in pre-roll is playing. Global
   * so the transport ARM button (which doesn't know which pad is
   * recording) can show a "counting in" state instead of full REC.
   * Written by `useRecordToPad` as it syncs its underlying
   * `useAudioRecording.countingIn`. Cleared when the real capture
   * begins. Ephemeral.
   */
  recordingCountingIn: boolean
  /**
   * Seconds remaining in the armed pad's count-in. Mirrors
   * `useAudioRecording.countInRemainingSec` for the active pad so
   * global UI can render a countdown. `0` when not counting in.
   * Ephemeral.
   */
  recordingCountInRemainingSec: number

  /**
   * Pad "edit mode" toggle. When off (default), tapping a pad
   * triggers its sound — the performance-first default. When on,
   * tapping a pad selects + focuses it for editing in the
   * Inspector / editor pane without firing the sound. Long-press
   * always behaves as edit-mode-tap regardless of this flag, so
   * users can pop the editor open without leaving performance mode.
   *
   * Ephemeral — a session-level toggle, not a durable preference.
   * Resets to off on reload so users always start in the
   * performance-ready state.
   */
  padEditMode: boolean

  /**
   * Bussboard canvas visual skin — `clean` (default) or
   * `switchboard` (opt-in patchbay aesthetic). Persisted so the
   * user's choice survives reloads. Changing this flag is
   * non-destructive: it flips the canvas render path without
   * touching any project / environment data.
   */
  canvasTheme: CanvasTheme

  /**
   * Active main-pane tab in the sequencer — drives which view renders
   * under the tab row (grid layouts / piano roll / automation /
   * plugin). See `MainPaneTab` above for the full rationale.
   */
  mainPaneTab: MainPaneTab

  /**
   * Plugin whose full-pane UI is loaded in the Plugin tab. `null`
   * when the tab is showing its empty state. See `SelectedPlugin`
   * above.
   */
  selectedPlugin: SelectedPlugin | null

  /**
   * Whether the currently-active main-pane tab is rendered in the
   * full-screen modal overlay. Triggered by the `⤢` button in the
   * tab row; closed via the modal's close button / ESC / overlay
   * click. Ephemeral — see `DEFAULT_FULL_SCREEN` above.
   */
  fullScreen: boolean

  /** See `PadMode` above — tri-state routing for pad click handling. */
  padMode: PadMode

  /** Scrub Pad is currently playing or latched — the trigger path
   *  uses this to suppress pad audio on `scrubTargetBankId`. */
  scrubEngaged: boolean

  /** Bank whose pad audio gets overridden while `scrubEngaged`. Null
   *  = no override (legacy behaviour, pattern + scrub play in
   *  parallel). See the Scrub Pad panel bank picker. */
  scrubTargetBankId: BankId | null

  /** Right-side performance FX rail (Scrambler + FxBank) collapse
   *  state. Persisted. */
  fxRailCollapsed: boolean

  setViewMode: (mode: ViewMode) => void
  toggleSelection: (id: string) => void
  clearSelection: () => void
  setSelection: (ids: string[]) => void
  togglePortGroup: (portId: string) => void
  setTheme: (theme: ThemeId) => void
  setFocusedZoneId: (zoneId: string | null) => void
  toggleAiPanel: () => void
  setShowGrid: (show: boolean) => void
  setRecordingPadIndex: (padIndex: number | null) => void
  setRecordArmNext: (v: boolean) => void
  setRecordCountInBars: (bars: CountInBars) => void
  setRecordingCountInState: (countingIn: boolean, remainingSec: number) => void
  setPadEditMode: (on: boolean) => void
  togglePadEditMode: () => void
  setCanvasTheme: (theme: CanvasTheme) => void
  toggleCanvasTheme: () => void
  setMainPaneTab: (tab: MainPaneTab) => void
  setSelectedPlugin: (plugin: SelectedPlugin | null) => void
  setFullScreen: (on: boolean) => void
  setPadMode: (mode: PadMode) => void
  setScrubEngaged: (engaged: boolean) => void
  setScrubTargetBankId: (bankId: BankId | null) => void
  setFxRailCollapsed: (collapsed: boolean) => void
  toggleFxRailCollapsed: () => void
}

export const useUIStore = create<UIStoreState>(set => ({
  viewMode: 'graph',
  selectedIds: new Set(),
  expandedPortGroups: new Set(),
  theme: initialTheme,
  focusedZoneId: null,
  aiPanelOpen: false,
  showGrid: true,
  recordingPadIndex: null,
  recordArmNext: false,
  recordCountInBars: readStoredCountIn(),
  recordingCountingIn: false,
  recordingCountInRemainingSec: 0,
  padEditMode: false,
  canvasTheme: readStoredCanvasTheme(),
  mainPaneTab: readStoredMainPaneTab(),
  selectedPlugin: readStoredSelectedPlugin(),
  fullScreen: DEFAULT_FULL_SCREEN,
  padMode: DEFAULT_PAD_MODE,
  scrubEngaged: false,
  scrubTargetBankId: null,
  fxRailCollapsed: readStoredFxRailCollapsed(),

  setViewMode: mode => set({ viewMode: mode }),

  toggleSelection: id =>
    set(state => {
      const newSet = new Set(state.selectedIds)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return { selectedIds: newSet }
    }),

  clearSelection: () => set({ selectedIds: new Set() }),

  setSelection: ids => set({ selectedIds: new Set(ids) }),

  togglePortGroup: portId =>
    set(state => {
      const newSet = new Set(state.expandedPortGroups)
      if (newSet.has(portId)) {
        newSet.delete(portId)
      } else {
        newSet.add(portId)
      }
      return { expandedPortGroups: newSet }
    }),

  setTheme: theme => {
    set({ theme })
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, theme)
      } catch {
        /* quota or privacy mode — in-memory only for this session */
      }
    }
  },

  setFocusedZoneId: zoneId => set({ focusedZoneId: zoneId }),

  toggleAiPanel: () => set(state => ({ aiPanelOpen: !state.aiPanelOpen })),

  setShowGrid: show => set({ showGrid: show }),

  setRecordingPadIndex: padIndex => set({ recordingPadIndex: padIndex }),

  setRecordArmNext: v => set({ recordArmNext: v }),

  setRecordCountInBars: bars => {
    set({ recordCountInBars: bars })
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(COUNT_IN_STORAGE_KEY, String(bars))
      } catch {
        /* quota / privacy mode — in-memory only for this session */
      }
    }
  },

  setRecordingCountInState: (countingIn, remainingSec) =>
    set({
      recordingCountingIn: countingIn,
      recordingCountInRemainingSec: remainingSec,
    }),

  setPadEditMode: on => set({ padEditMode: on }),

  togglePadEditMode: () => set(state => ({ padEditMode: !state.padEditMode })),

  setCanvasTheme: theme => {
    set({ canvasTheme: theme })
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(CANVAS_THEME_STORAGE_KEY, theme)
      } catch {
        /* quota / privacy mode — in-memory only for this session */
      }
    }
  },

  toggleCanvasTheme: () =>
    set(state => {
      const next: CanvasTheme =
        state.canvasTheme === 'switchboard' ? 'clean' : 'switchboard'
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(CANVAS_THEME_STORAGE_KEY, next)
        } catch {
          /* quota / privacy mode — in-memory only for this session */
        }
      }
      return { canvasTheme: next }
    }),

  setMainPaneTab: tab => {
    set({ mainPaneTab: tab })
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(MAIN_PANE_TAB_STORAGE_KEY, tab)
      } catch {
        /* quota / privacy mode — in-memory only for this session */
      }
    }
  },

  setSelectedPlugin: plugin => {
    set({ selectedPlugin: plugin })
    if (typeof window !== 'undefined') {
      try {
        if (plugin) {
          window.localStorage.setItem(
            SELECTED_PLUGIN_STORAGE_KEY,
            JSON.stringify(plugin),
          )
        } else {
          window.localStorage.removeItem(SELECTED_PLUGIN_STORAGE_KEY)
        }
      } catch {
        /* quota / privacy mode — in-memory only for this session */
      }
    }
  },

  setFullScreen: on => set({ fullScreen: on }),

  setPadMode: mode => set({ padMode: mode }),

  setScrubEngaged: engaged => set({ scrubEngaged: engaged }),
  setScrubTargetBankId: bankId => set({ scrubTargetBankId: bankId }),

  setFxRailCollapsed: collapsed => {
    set({ fxRailCollapsed: collapsed })
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(
          FX_RAIL_COLLAPSED_STORAGE_KEY,
          collapsed ? '1' : '0',
        )
      } catch {
        /* quota / privacy — in-memory only */
      }
    }
  },

  toggleFxRailCollapsed: () =>
    set(state => {
      const next = !state.fxRailCollapsed
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(
            FX_RAIL_COLLAPSED_STORAGE_KEY,
            next ? '1' : '0',
          )
        } catch {
          /* in-memory only */
        }
      }
      return { fxRailCollapsed: next }
    }),
}))
